import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { CfFtpInfo } from 'data/cfFtpInfo/cfFtpInfo.types';
import CfFtpInfoServerForm from 'routes/CfFtpInfo/components/CfFtpInfoServerForm';
import * as types from 'data/cfFtpInfo/cfFtpInfo.types';
import { cfFtpInfoTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptCfFtpInfoTemplate,
} from '../../utils/cfFtpInfoTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/cfFtpInfoTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.CfFtpInfo
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  cfFtpInfoTemplateDef: adaptTemplateVisibilityMode(cfFtpInfoTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedCfFtpInfo: cfFtpInfoSelectors
    .getSelectedCfFtpInfo(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedCfFtpInfo: cfFtpInfoSelectors
    .getSelectedCfFtpInfo(state)[0],
  cfFtpInfo: cfFtpInfoSelectors.getCfFtpInfo(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToCfFtpInfoList: cfFtpInfoActions.goToCfFtpInfoList,
  updateCfFtpInfo: cfFtpInfoActions.updateCfFtpInfo,
  getCfFtpInfo: cfFtpInfoActions.getCfFtpInfo,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CfFtpInfoEdit = ({
  history,
  selectedCfFtpInfo,
  originalSelectedCfFtpInfo,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  cfFtpInfoTemplateDef,
  cfFtpInfo,
  goToCfFtpInfoList,
  updateCfFtpInfo,
  getCfFtpInfo,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<CfFtpInfo>>({});
  const [template, setTemplate] = useState(cfFtpInfoTemplateDef);
  const { t } = useTranslation();

  const id = selectedCfFtpInfo?.id;

  useEffect(() => {
    if (id) {
      getCfFtpInfo(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getCfFtpInfo]);

  useEffect(() => {
    if (cfFtpInfo != null && template != null) {
      const clonedCfFtpInfo = cloneDeep(cfFtpInfo);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
      setFormData(clonedCfFtpInfo);
    }
  }, [cfFtpInfo, id]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const dialedCfFtpInfoData = formRef.current.getValues();
    const payload = {
      data: dialedCfFtpInfoData,
      auditDetails: getAuditDetailsToSubmit(
        dialedCfFtpInfoData,
        originalSelectedCfFtpInfo,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload && cfFtpInfo != null) {
      updateCfFtpInfo(deliveryFunctionId, cfFtpInfo?.id, payload, history);
    }
  };

  const handleCancel = () => goToCfFtpInfoList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as CfFtpInfo);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptCfFtpInfoTemplate(clonedTemplate);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <CfFtpInfoServerForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (cfFtpInfo == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="CfFtpInfoEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('cfFtpInfo.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(CfFtpInfoEdit));
